import { ReactNode } from "react";
import { useMonth, CalendarDay, dayLabelFormat, weekdayLabelFormat, monthLabelFormat } from "@datepicker-react/hooks";
import { format } from "date-fns";
import { usePathname, useSearchParams } from "next/navigation";
import { DayStatus, NewDayObject } from "commonModule/calendar";
import { getVisibleCalendarDays } from "components/date-picker/datepicker/get-visible-calendar-days";
import { selectUser } from "stores/auth/authSlice";
import { useAppSelector } from "stores/hooks";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import Day from "./day";
interface MonthProps {
  annotatedCalendarDates?: Record<string, NewDayObject>;
  isAnnotated: boolean;
  maxBookingDate?: Date;
  month: number;
  nextButton: ReactNode | undefined;
  prevButton: ReactNode | undefined;
  year: number;
}
// Component for rendering day labels in a calendar month.
export const DayLabels = ({
  days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  className = ""
}: {
  days?: string[];
  className?: string;
}) => <div className={`my-5 grid grid-cols-[repeat(7,1fr)] items-center justify-center sm:grid-cols-[repeat(7,48px)] lg:my-0 ${className}`} data-sentry-component="DayLabels" data-sentry-source-file="month.tsx">
    {days && days?.map(dayLabel => <div key={dayLabel} className='flex items-center justify-center text-sm font-semibold leading-[14px] text-Gray-700 lg:leading-[30px]'>
          {dayLabel}
        </div>)}
  </div>;
function Month({
  year,
  month,
  annotatedCalendarDates,
  nextButton = null,
  prevButton = null,
  isAnnotated = false,
  maxBookingDate
}: MonthProps) {
  const pathname = usePathname();
  const isFavoritesPage = pathname?.startsWith("/favorites");
  const {
    days,
    weekdayLabels,
    monthLabel
  } = useMonth({
    dayLabelFormat,
    monthLabelFormat,
    weekdayLabelFormat,
    year,
    month,
    firstDayOfWeek: 0
  });
  const [isSmallerThan767] = useMediaQuery("(max-width: 767px)");
  const user = useAppSelector(selectUser);
  const isAdmin = user?.isAdmin;
  const searchParams = useSearchParams();
  const allowedSameDay = searchParams?.get("allowSameDay") || "";
  const isDisabledDate = (date: Date) => {
    const now = new Date();

    // ✅ Pure local date-part comparison
    const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
    if (isToday && (isAdmin || allowedSameDay)) return false;

    // ✅ Construct local midnights — no setHours, no UTC, no timestamps
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (dateMidnight <= todayMidnight) return true;
    if (maxBookingDate) {
      const maxMidnight = new Date(maxBookingDate.getFullYear(), maxBookingDate.getMonth(), maxBookingDate.getDate());
      if (dateMidnight > maxMidnight) return true;
    }
    return false;
  };
  const visibleDays = getVisibleCalendarDays(days, year, month, 0);
  return <div className='flex flex-col justify-center sm:items-center' data-sentry-component="Month" data-sentry-source-file="month.tsx">
      <div className={`flex w-full max-w-[350px] justify-start sm:px-1 lg:max-w-full lg:justify-between lg:py-2 ${isFavoritesPage ? "items-start lg:items-center" : "items-start"}`}>
        <div className='lg:w-10'>{prevButton}</div>
        <p className={`px-4 py-2 font-semibold msm:py-2 sm:px-2 ${isFavoritesPage ? "text-sm text-Gray-700 lg:text-brand-dark" : "text-base text-Gray-700 lg:text-xl"}`}>
          {monthLabel}
        </p>
        <div className='lg:w-10'>{nextButton}</div>
      </div>
      <div className='hidden lg:grid'>
        <DayLabels days={weekdayLabels} className={isFavoritesPage ? "sm:!grid-cols-[repeat(7,42px)]" : ""} data-sentry-element="DayLabels" data-sentry-source-file="month.tsx" />{" "}
        {/* Rendering DayLabels component */}
      </div>
      <div className={`grid grid-cols-[repeat(7,1fr)] items-center justify-center ${isFavoritesPage ? "sm:grid-cols-[repeat(7,42px)]" : "sm:grid-cols-[repeat(7,48px)]"}`}>
        {(isSmallerThan767 ? visibleDays : days).map((day: CalendarDay, index: number) => {
        if (typeof day === "object") {
          // it's for date-range-input-general
          if (!isAnnotated) {
            return <Day date={day.date} annotatedCalendarDay={null} key={day.dayLabel} isDisabledDate={isDisabledDate(day.date)} />;
          }

          // it's for date-range-input-detail [detail page]
          const annotatedDay = annotatedCalendarDates?.[format(day.date, "yyyy-MM-dd")] || {
            valid_from: format(day.date, "yyyy-MM-dd"),
            base_price: 0,
            available: false,
            min_nights: annotatedCalendarDates?.[0]?.min_nights || 1,
            max_nights: annotatedCalendarDates?.[0]?.max_nights || 365,
            arrival: false,
            departure: true,
            isDisabled: true,
            status: DayStatus.STRIKED,
            maximumBookingDuration: 0
          };
          if (annotatedDay) {
            return <Day date={day.date} annotatedCalendarDay={annotatedDay} key={day.dayLabel} isDisabledDate={isDisabledDate(day.date)} />;
          } else {
            return <div className='bg-red-500' key={index} />;
          }
        }
        return <div key={index} />;
      })}
      </div>
    </div>;
}
export default Month;